const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/reducerUtils.ts';
import Imm from 'immutable';
import { Logger } from '../services/KisLoggerService';
import { assertNever } from './generalUtils';

const Log = Logger('common/reducerUtils');

/**
 * Increases a counter value, e.g. a loading counter.
 * @param state Current state in which the counter has to be increased
 * @param path Path to the location of loading counter
 * @returns State with increased counter
 */
export function incCounter(state: Imm.Map<string, any>, path: string[]) {
    const count: number = state.getIn(path) || 0;
    if (typeof count !== 'number') throw new Error(`incCounter: Not a number: ${count} <${typeof count}>`);
    return state.setIn(path, count + 1);
}

/**
 * Decreases a counter value, e.g. a loading counter.
 * @param state Current state in which the counter has to be decreased
 * @param path Path to the location of loading counter (usually includes PATNR)
 * @param del One of:
 *    'keep-zero': do not delete counter when it hits zero;
 *    'delete-zero': delete counter when it hits zero (default);
 *    'delete-parent': delete counter when it hits zero, and parent as well if it gets empty.
 * @returns State with decreased counter
 */
export function decCounter(
    state: Imm.Map<string, any>,
    path: string[],
    del: 'keep-zero' | 'delete-zero' | 'delete-parent' = 'delete-zero'
) {
    let count: number = state.getIn(path) || 0;

    if (typeof count !== 'number') {
        throw new Error(`decCounter: Not a number: ${count} <${typeof count}>`);
    }

    count = count - 1;

    if (count > 0) {
        return state.setIn(path, count);
    }

    if (count < 0) {
        Log.error('decCounter(): Unbalanced counter at ', path);
        return state.setIn(path, count);
    }

    // The counter has become zero.

    switch (del) {
        case 'keep-zero':
            return state.setIn(path, 0);

        case 'delete-zero':
            return state.deleteIn(path);

        case 'delete-parent': {
            state = state.deleteIn(path);
            const parentPath = path.slice(0, path.length - 1);

            if (!state.getIn(parentPath) || state.getIn(parentPath).isEmpty()) {
                return state.deleteIn(parentPath);
            } else {
                return state;
            }
        }

        default:
            assertNever(del);
    }
}
